import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Panel, DataTable, StatusPill, money, fmtDate, inputCls } from "./shared";
import InvoiceDialog from "./InvoiceDialog";

const shipLink = (r) => (
  <Link to={`/shipments/${r.shipment_id}`} className="font-mono-ops text-xs text-sky-300 hover:text-sky-200">{r.shipment_number}</Link>
);

export default function InvoicesTab({ onChanged = () => {} }) {
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => {
    try {
      const [i, r] = await Promise.all([api.get("/finance/invoices"), api.get("/finance/ready-to-bill")]);
      setRows(i.data); setReady(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChanged(); };
  const n = q.toLowerCase();
  const filtered = rows.filter((r) => !n || [r.invoice_number, r.shipment_number, r.client_name].some((v) => v?.toLowerCase().includes(n)));

  const readyCols = [
    { key: "shipment_number", label: "Shipment", render: shipLink },
    { key: "type", label: "Folder", render: (r) => <span className="capitalize">{r.type}</span> },
    { key: "mawb_number", label: "MAWB #", mono: true },
    { key: "transmittal_number", label: "Transmittal", mono: true },
    { key: "client_name", label: "Client" },
    { key: "biller_name", label: "Biller" },
    { key: "act", label: "", render: (r) => <InvoiceDialog shipmentId={r.shipment_id} label="Create invoice" testid={`button-create-invoice-${r.shipment_number}`} onSaved={refresh} /> },
  ];
  const cols = [
    { key: "invoice_number", label: "Invoice #", mono: true },
    { key: "shipment_number", label: "Shipment", render: shipLink },
    { key: "client_name", label: "Client" },
    { key: "issued_at", label: "Issued", render: (r) => fmtDate(r.issued_at) },
    { key: "due_date", label: "Due", render: (r) => fmtDate(r.due_date) },
    { key: "total", label: "Total", right: true, render: (r) => money(r.total) },
    { key: "collected", label: "Collected", right: true, render: (r) => money(r.collected) },
    { key: "outstanding", label: "Outstanding", right: true, render: (r) => money(r.outstanding) },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "act", label: "", render: (r) => <InvoiceDialog shipmentId={r.shipment_id} label="Open" testid={`button-open-invoice-${r.invoice_number}`} onSaved={refresh} /> },
  ];

  return (
    <div className="space-y-6">
      <Panel title="Ready to bill" subtitle="Billing Transmittals that the assigned biller has received and acknowledged.">
        <DataTable testid="ready-to-bill-table" columns={readyCols} rows={ready} empty="Nothing waiting. Acknowledged transmittals will appear here." />
      </Panel>
      <Panel title="Billed clients & invoices" subtitle="Every invoice, linked to its shipment, with its collection status."
             right={
               <div className="relative w-full sm:w-72">
                 <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                 <Input data-testid="input-invoice-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice, shipment, client…" className={`pl-9 ${inputCls}`} />
               </div>
             }>
        <DataTable testid="invoices-table" columns={cols} rows={filtered} empty="No invoices yet." />
      </Panel>
    </div>
  );
}
